import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  message,
  Popconfirm,
  Statistic,
  Tooltip,
  Descriptions,
  Badge,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  SearchOutlined,
  InfoCircleOutlined 
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface Tenant {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country: string;
  status: 'active' | 'suspended' | 'inactive';
  max_vehicles: number;
  max_drivers: number;
  max_missions_per_month: number;
  features: Record<string, any>;
  created_at: string;
  updated_at: string;
  stats: {
    vehicle_count: number;
    driver_count: number;
    total_missions: number;
    completed_missions: number;
  };
}

interface TenantFormData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  max_vehicles: number;
  max_drivers: number;
  max_missions_per_month: number;
  admin_email: string;
  admin_password: string;
  admin_first_name: string;
  admin_last_name: string;
}

const SuperAdminTenants: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [viewingTenant, setViewingTenant] = useState<Tenant | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle navigation state from dashboard
  useEffect(() => {
    const state = location.state as any;
    if (state?.action === 'create') {
      handleCreate();
      // Clear the navigation state to prevent reopening on refresh
      navigate('/super-admin/tenants', { replace: true });
    } else if (state?.action === 'view' && state?.selectedTenantId) {
      // Find and show the tenant details
      const tenantId = state.selectedTenantId;
      // We'll handle this after the tenants data is loaded
      if (tenants.length > 0) {
        const tenant = tenants.find(t => t.id === tenantId);
        if (tenant) {
          handleViewDetails(tenant);
          // Clear the navigation state
          navigate('/super-admin/tenants', { replace: true });
        }
      }
    }
  }, [location.state, navigate]);

  // Récupération des entreprises
  const { data: tenantsData, isLoading } = useQuery({
    queryKey: ['tenants', searchText, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (statusFilter) params.append('status', statusFilter);

      const response = await apiClient.get(`/super-admin/tenants?${params.toString()}`);
      return response;
    },
  });

  const tenants: Tenant[] = tenantsData?.tenants || [];

  // Handle navigation state after tenants are loaded
  useEffect(() => {
    const state = location.state as any;
    if (state?.action === 'view' && state?.selectedTenantId && tenants.length > 0) {
      const tenant = tenants.find(t => t.id === state.selectedTenantId);
      if (tenant) {
        handleViewDetails(tenant);
        navigate('/super-admin/tenants', { replace: true });
      }
    }
  }, [tenants, location.state, navigate]);

  // Mutation pour créer une entreprise
  const createTenantMutation = useMutation({
    mutationFn: async (data: TenantFormData) => {
      const response = await apiClient.post('/super-admin/tenants', data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Entreprise créée avec succès');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la création');
    },
  });

  // Mutation pour modifier une entreprise
  const updateTenantMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TenantFormData> }) => {
      const response = await apiClient.put(`/super-admin/tenants/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      message.success('Entreprise modifiée avec succès');
      setIsModalVisible(false);
      setEditingTenant(null);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la modification');
    },
  });

  // Mutation pour supprimer une entreprise
  const deleteTenantMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/super-admin/tenants/${id}`);
    },
    onSuccess: () => {
      message.success('Entreprise supprimée avec succès');
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    },
  });

  // Gestionnaires d'événements
  const handleCreate = () => {
    setEditingTenant(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (tenant: Tenant) => {
    setEditingTenant(tenant);
    form.setFieldsValue({
      ...tenant,
      admin_email: '',
      admin_password: '',
      admin_first_name: '',
      admin_last_name: '',
    });
    setIsModalVisible(true);
  };

  const handleViewDetails = (tenant: Tenant) => {
    setViewingTenant(tenant);
    setIsDetailModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteTenantMutation.mutate(id);
  };

  const handleSubmit = (values: TenantFormData) => {
    if (editingTenant) {
      // Exclure les champs admin lors de la modification
      const { admin_email, admin_password, admin_first_name, admin_last_name, ...updateData } = values;
      updateTenantMutation.mutate({ id: editingTenant.id, data: updateData });
    } else {
      createTenantMutation.mutate(values);
    }
  };

  // Colonnes du tableau
  const columns = [
    {
      title: 'Entreprise',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Tenant) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
          {record.city && (
            <div style={{ fontSize: '12px', color: '#999' }}>{record.city}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'active' ? 'green' : status === 'suspended' ? 'orange' : 'red';
        const text = status === 'active' ? 'Actif' : status === 'suspended' ? 'Suspendu' : 'Inactif';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Statistiques',
      key: 'stats',
      render: (_: any, record: Tenant) => (
        <div style={{ fontSize: '12px' }}>
          <div>{record.stats.vehicle_count} véhicules</div>
          <div>{record.stats.driver_count} chauffeurs</div>
          <div>{record.stats.total_missions} missions</div>
        </div>
      ),
    },
    {
      title: 'Limites',
      key: 'limits',
      render: (_: any, record: Tenant) => (
        <div style={{ fontSize: '12px' }}>
          <div>Max véhicules: {record.max_vehicles}</div>
          <div>Max chauffeurs: {record.max_drivers}</div>
          <div>Max missions/mois: {record.max_missions_per_month}</div>
        </div>
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Tenant) => (
        <Space>
          <Tooltip title="Voir détails">
            <Button 
              type="link" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            >
              Voir
            </Button>
          </Tooltip>
          <Tooltip title="Modifier">
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Modifier
            </Button>
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette entreprise ?"
            description="Cette action est irréversible et supprimera toutes les données associées."
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button 
                type="link" 
                size="small" 
                icon={<DeleteOutlined />}
                danger
              >
                Supprimer
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Gestion des Entreprises</Title>
        <p style={{ color: '#666', margin: 0 }}>
          Gérer les entreprises clientes de la plateforme FleetBase
        </p>
      </div>

      {/* Filtres et recherche */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Rechercher une entreprise..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6}>
            <Select
              placeholder="Filtrer par statut"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
            >
              <Option value="">Tous les statuts</Option>
              <Option value="active">Actif</Option>
              <Option value="suspended">Suspendu</Option>
              <Option value="inactive">Inactif</Option>
            </Select>
          </Col>
          <Col xs={24} sm={10} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Nouvelle entreprise
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Tableau des entreprises */}
      <Card>
        <Table
          columns={columns}
          dataSource={tenants}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} sur ${total} entreprises`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Modal de création/modification */}
      <Modal
        title={editingTenant ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingTenant(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            country: 'FR',
            max_vehicles: 10,
            max_drivers: 20,
            max_missions_per_month: 1000,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="Nom de l'entreprise"
                name="name"
                rules={[{ required: true, message: 'Nom requis' }]}
              >
                <Input placeholder="Ex: Transport Dupont" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Email requis' },
                  { type: 'email', message: 'Format email invalide' }
                ]}
              >
                <Input placeholder="contact@entreprise.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item label="Téléphone" name="phone">
                <Input placeholder="+33 1 23 45 67 89" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ville" name="city">
                <Input placeholder="Lyon" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Code postal" name="postal_code">
                <Input placeholder="69000" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={16}>
              <Form.Item label="Adresse" name="address">
                <Input placeholder="123 Rue de la République" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Pays" name="country">
                <Select>
                  <Option value="FR">France</Option>
                  <Option value="BE">Belgique</Option>
                  <Option value="CH">Suisse</Option>
                  <Option value="LU">Luxembourg</Option>
                  <Option value="DE">Allemagne</Option>
                  <Option value="ES">Espagne</Option>
                  <Option value="IT">Italie</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={8}>
              <Form.Item
                label="Max véhicules"
                name="max_vehicles"
                rules={[{ required: true, message: 'Valeur requise' }]}
              >
                <Input type="number" min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Max chauffeurs"
                name="max_drivers"
                rules={[{ required: true, message: 'Valeur requise' }]}
              >
                <Input type="number" min={1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Max missions/mois"
                name="max_missions_per_month"
                rules={[{ required: true, message: 'Valeur requise' }]}
              >
                <Input type="number" min={1} />
              </Form.Item>
            </Col>
          </Row>

          {!editingTenant && (
            <>
              <Title level={4} style={{ marginTop: '24px', marginBottom: '16px' }}>
                Administrateur de l'entreprise
              </Title>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    label="Prénom admin"
                    name="admin_first_name"
                    rules={[{ required: true, message: 'Prénom requis' }]}
                  >
                    <Input placeholder="Jean" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Nom admin"
                    name="admin_last_name"
                    rules={[{ required: true, message: 'Nom requis' }]}
                  >
                    <Input placeholder="Dupont" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    label="Email admin"
                    name="admin_email"
                    rules={[
                      { required: true, message: 'Email requis' },
                      { type: 'email', message: 'Format email invalide' }
                    ]}
                  >
                    <Input placeholder="admin@entreprise.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Mot de passe"
                    name="admin_password"
                    rules={[
                      { required: true, message: 'Mot de passe requis' },
                      { min: 8, message: 'Au moins 8 caractères' }
                    ]}
                  >
                    <Input.Password placeholder="Mot de passe sécurisé" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <div style={{ textAlign: 'right', marginTop: '24px' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createTenantMutation.isPending || updateTenantMutation.isPending}
              >
                {editingTenant ? 'Modifier' : 'Créer'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Modal de détails */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            Détails de l'entreprise
          </Space>
        }
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setViewingTenant(null);
        }}
        footer={[
          <Button key="edit" type="primary" onClick={() => {
            setIsDetailModalVisible(false);
            if (viewingTenant) handleEdit(viewingTenant);
          }}>
            Modifier
          </Button>,
          <Button key="close" onClick={() => {
            setIsDetailModalVisible(false);
            setViewingTenant(null);
          }}>
            Fermer
          </Button>,
        ]}
        width={800}
      >
        {viewingTenant && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small" title="Informations générales">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nom">{viewingTenant.name}</Descriptions.Item>
                    <Descriptions.Item label="Email">{viewingTenant.email}</Descriptions.Item>
                    <Descriptions.Item label="Téléphone">{viewingTenant.phone || 'Non renseigné'}</Descriptions.Item>
                    <Descriptions.Item label="Adresse">
                      {viewingTenant.address ? (
                        <div>
                          <div>{viewingTenant.address}</div>
                          <div>{viewingTenant.postal_code} {viewingTenant.city}</div>
                          <div>{viewingTenant.country}</div>
                        </div>
                      ) : 'Non renseignée'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut">
                      <Badge 
                        status={viewingTenant.status === 'active' ? 'success' : viewingTenant.status === 'suspended' ? 'warning' : 'error'}
                        text={viewingTenant.status === 'active' ? 'Actif' : viewingTenant.status === 'suspended' ? 'Suspendu' : 'Inactif'}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" title="Statistiques">
                  <Row gutter={[8, 8]}>
                    <Col span={12}>
                      <Statistic title="Véhicules" value={viewingTenant.stats.vehicle_count} />
                    </Col>
                    <Col span={12}>
                      <Statistic title="Chauffeurs" value={viewingTenant.stats.driver_count} />
                    </Col>
                    <Col span={12}>
                      <Statistic title="Missions totales" value={viewingTenant.stats.total_missions} />
                    </Col>
                    <Col span={12}>
                      <Statistic title="Missions terminées" value={viewingTenant.stats.completed_missions} />
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Card size="small" title="Limites et configuration">
                  <Row gutter={[16, 16]}>
                    <Col span={8}>
                      <Statistic 
                        title="Max véhicules" 
                        value={viewingTenant.max_vehicles}
                        suffix={`/ ${viewingTenant.stats.vehicle_count} utilisés`}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic 
                        title="Max chauffeurs" 
                        value={viewingTenant.max_drivers}
                        suffix={`/ ${viewingTenant.stats.driver_count} utilisés`}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic 
                        title="Max missions/mois" 
                        value={viewingTenant.max_missions_per_month}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: '16px' }}>
                    <strong>Créé le:</strong> {dayjs(viewingTenant.created_at).format('DD/MM/YYYY à HH:mm')}
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <strong>Dernière modification:</strong> {dayjs(viewingTenant.updated_at).format('DD/MM/YYYY à HH:mm')}
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuperAdminTenants;